import React, { useState, useEffect, useCallback } from 'react';
import { LuCircleAlert, LuCircleCheck, LuInfo, LuTriangleAlert, LuX } from 'react-icons/lu';

const toastStyles = {
  success: { icon: LuCircleCheck, iconClass: 'text-emerald-600 dark:text-emerald-400' },
  warning: { icon: LuTriangleAlert, iconClass: 'text-amber-600 dark:text-amber-400' },
  info: { icon: LuInfo, iconClass: 'text-blue-600 dark:text-blue-400' },
  error: { icon: LuCircleAlert, iconClass: 'text-red-600 dark:text-red-400' },
};

/**
 * Reusable Toast Component
 * Shows a short message at the bottom centre of the screen and hides itself after `duration`.
 */
const Toast = ({
  message,
  type = 'error',
  duration = 4000,
  onClose,
  visible = false
}) => {
  const [isVisible, setIsVisible] = useState(visible);

  useEffect(() => {
    setIsVisible(visible);

    if (visible && duration > 0) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(() => onClose?.(), 300); // Wait for animation to complete
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [visible, duration, onClose]);

  if (!isVisible && !visible) return null;

  const style = toastStyles[type] || toastStyles.error;
  const Icon = style.icon;

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-6 z-50 flex justify-center px-4">
      <div
        role={type === 'error' ? 'alert' : 'status'}
        className={`pointer-events-auto flex w-full max-w-md items-start gap-3 rounded-xl border border-ui-line bg-ui-card px-4 py-3 shadow-pop transition-all duration-300 ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
        }`}
      >
        <Icon aria-hidden className={`mt-0.5 size-[18px] shrink-0 ${style.iconClass}`} />
        <p className="flex-1 text-sm font-medium text-ui-ink">{message}</p>
        <button
          type="button"
          aria-label="Close"
          onClick={() => {
            setIsVisible(false);
            setTimeout(() => onClose?.(), 300);
          }}
          className="-mr-1 flex size-6 shrink-0 items-center justify-center rounded text-ui-ink-3 transition-colors hover:bg-ui-hover hover:text-ui-ink"
        >
          <LuX aria-hidden className="size-4" />
        </button>
      </div>
    </div>
  );
};

/**
 * Toast Hook for easier usage
 */
export const useToast = () => {
  const [toast, setToast] = useState(null);

  const showToast = useCallback((message, type = 'error', duration = 4000) => {
    setToast({ message, type, duration, visible: true });
  }, []);

  const hideToast = useCallback(() => {
    setToast(previousToast => previousToast ? { ...previousToast, visible: false } : null);
  }, []);

  const ToastComponent = toast ? (
    <Toast
      {...toast}
      onClose={() => setToast(null)}
    />
  ) : null;

  return {
    showToast,
    hideToast,
    ToastComponent
  };
};

export default Toast;
